import "./EqualWork.css"

import apiEndpoints  from "constants/endpoints.constants"
import { ANALYZE_WAGE_GAP } from "constants/text.constants"

import { useState, useEffect } from "react"

import { fetchMixedGroups } from "services/fetchers"
import { downloadCsvFile, downloadWordFile } from "utils/downloadFile";

import { mixedSelcetions } from "types/sharedTypes"

import Error from "components/atoms/error/Error"
import DropdownButton from "components/atoms/dropdownButton/DropdownButton"
import CommentsModal from "components/cores/commentsModal/CommentsModal"
import ContentSkeleton from "components/atoms/contentSkeleton/ContentSkeleton"

import DiagramModal from "./diagramModal/DiagramModal"
import EqualWorkTable from "./equalWorkTable/EqualWorkTable"

interface IEqualWork {
    analysisId: string
    registryName: string
    salaryType: number
    setActiveSteps: React.Dispatch<React.SetStateAction<number[]>>
}

const EqualWork = ({ analysisId, registryName, salaryType, setActiveSteps }: IEqualWork) => {
    const [fetchError, setFetchError] = useState("")
    const [postError, setPostError] = useState("")

    const [equalWorkGroups, setEqualWorkGroups] = useState<mixedSelcetions[]>([])
    const [fetchingEqualWorkGroups, setFetchingEqualWorkGroups] = useState(true)
    const [noRelevantIndividuals, setNoRelevantIndividuals] = useState(false)

    const [downloadingCSV, setDownloadingCSV] = useState(false)
    const [downloadingWord, setDownloadingWord] = useState(false)

    // const [commentedGroupsIds, setCommentedGroupsIds] = useState<string[]>([])
    const [fetchingAllComments, setFetchingAllComments] = useState(true)

    const [hasCommentChanged, setHasCommentChanged] = useState(false)

    const [showCommentsModal, setShowCommentsModal] = useState(false)
    const [showDiagramModal, setShowDiagramModal] = useState(false)

    const [currentGroupInfo, setCurrentGroupInfo] = useState({ Name: "", Id: "" })

    const downloadCSV = () => {
        downloadCsvFile({
            endpoint: apiEndpoints().analyzeExportTableStep3,
            filename: ANALYZE_WAGE_GAP.S3_FILENAME,
            body: { AnalyseId: analysisId },
            setIsDownloading: setDownloadingCSV,
            setError: setPostError,
            errorText: ANALYZE_WAGE_GAP.ERROR_DOWNLOADING_CSV,
        });
    }

    const downloadWord = () => {
        downloadWordFile({
            endpoint: apiEndpoints().analyzeExportTableStep3,
            filename: ANALYZE_WAGE_GAP.S3_FILENAME_WORD,
            body: { AnalyseId: analysisId },
            setIsDownloading: setDownloadingWord,
            setError: setPostError,
            errorText: ANALYZE_WAGE_GAP.ERROR_DOWNLOADING_CSV,
            hasHeadlines: false
        });
    };    

    useEffect(() => {
        if (fetchingAllComments || hasCommentChanged) {
            fetchMixedGroups(analysisId, setEqualWorkGroups, setFetchingEqualWorkGroups, setFetchError).then(() => {
                setHasCommentChanged(false)
                setFetchingAllComments(false)
                setFetchError("")
            })
        }
    }, [analysisId, hasCommentChanged, fetchingAllComments])

    // useEffect(() => {
    //     if (equalWorkGroups.length) {
    //         const body = { AnalyseId: analysisId, CommentType: 1, Groups: equalWorkGroups }

    //         prepareCsrfToken().then((csrfToken) =>
    //             postWithBodyAndCsrf(api.analyzeCommentsForAllGroups, csrfToken, JSON.stringify(body))
    //                 .then((commentedGroupsArray) => {
    //                     setCommentedGroupsIds(commentedGroupsArray) // den här verkar inte användas alls sen i equalworktable
    //                     setFetchingAllComments(false)
    //                     setFetchError("")
    //                 })
    //                 .catch(() => {
    //                     setFetchingAllComments(false)
    //                     setFetchError(GENERAL_TEXTS.ERROR_TEXT_SAVING)
    //                 })
    //         )
    //     }
    //     setFetchingAllComments(false)
    // }, [analysisId, equalWorkGroups])

    useEffect(() => {
        if (equalWorkGroups.length) {
            setActiveSteps([1, 2, 3, 4])
            setNoRelevantIndividuals(false)
        } else {
            setActiveSteps([1, 2, 3])
            setNoRelevantIndividuals(true)
        }
    }, [equalWorkGroups, setActiveSteps])

    return (
        <>
            {(fetchingEqualWorkGroups || fetchingAllComments) && !fetchError && <ContentSkeleton />}
            {fetchError && <Error>{fetchError}</Error>}
            {postError && (
                <div className="mb16">
                    <Error>{postError}</Error>
                </div>
            )}
            {!fetchingEqualWorkGroups && !fetchingAllComments && !fetchError && (
                <div>
                    {!noRelevantIndividuals && (
                        <div className="flex gap8 jc-flex-end">
                            <DropdownButton
                                label={ANALYZE_WAGE_GAP.BUTTON_EXPORT}
                                isLoading={downloadingCSV || downloadingWord}
                                menuOptions={[
                                    { id: "excel", label: ANALYZE_WAGE_GAP.BUTTON_EXPORT_CSV, onClick: downloadCSV, },
                                    { id: "word", label: ANALYZE_WAGE_GAP.BUTTON_EXPORT_WORD, onClick: downloadWord },
                                ]}
                            />
                        </div>
                    )}
                    <h2>{ANALYZE_WAGE_GAP.S3_H2}</h2>
                    <div>
                        <p>{ANALYZE_WAGE_GAP.S3_STEP_EXPLANATION}</p>
                        {noRelevantIndividuals && <p>{ANALYZE_WAGE_GAP.S3_NO_RELEVANT_INDIVIDUALS}</p>}
                        {!noRelevantIndividuals && (
                            <EqualWorkTable
                                equalWorkGroups={equalWorkGroups}
                                // commentedGroupsIds={commentedGroupsIds}
                                setCurrentGroupInfo={setCurrentGroupInfo}
                                setShowCommentsModal={setShowCommentsModal}
                                setShowDiagramModal={setShowDiagramModal}
                            />
                        )}
                    </div>
                    {showCommentsModal && (
                        <CommentsModal
                            analysisId={analysisId}
                            groupInfo={currentGroupInfo}
                            showModal={showCommentsModal}
                            setShowModal={setShowCommentsModal}
                            setHasCommentChanged={setHasCommentChanged}
                        />
                    )}
                    {showDiagramModal && (
                        <DiagramModal
                            groupInfo={currentGroupInfo}
                            registryName={registryName}
                            salaryType={salaryType}
                            showModal={showDiagramModal}
                            setShowModal={setShowDiagramModal}
                            setShowCommentsModal={setShowCommentsModal}
                        />
                    )}
                </div>
            )}
        </>
    )
}

export default EqualWork
